// services/api/clientesApi.ts
import api from '../../utils/axios';
import { useState, useCallback } from 'react';

interface VentasDetailClienteResponse {
  ventasUltimos3Meses: {
    fecha: string;  // Nombre del mes
    monto: number;  // Cantidad de ventas
  }[];
  ultimas10Ventas: {
    id: number;
    fecha: string;
    monto: number;
    estado: string;
  }[];
}

interface UpdateDto extends ClienteDto {
  deleteImage?: boolean;
}

interface VentaResumen {
  id: number;
  fecha: string;
  monto: number;
  estado: string;
}

interface ClienteItem {
  Id: number;
  IdTipoCliente: number;
  IdTipoDocumento: number;
  NumeroDocumento: string;
  FechaRegistro: Date;
  FechaNacimiento: string;
  NombreCompleto: string;
  Telefono: string;
  Direccion: string;
  Correo: string;
  Imagen: string;
  Activo: boolean;
  Eliminado: boolean;
  tipoCliente?: { Id: number; Nombre: string };
  tipoDocumento?: { Id: number; Nombre: string };
}

interface ClienteDetailResponse {
  cliente: ClienteItem;
  ventasUltimos3Meses: {
    fecha: string;
    monto: number;
  }[];
  ultimas10Ventas: VentaResumen[];
}

interface ClienteDto {
  IdTipoCliente: number;
  IdTipoDocumento: number;
  NumeroDocumento: string;
  NombreCompleto: string;
  FechaNacimiento: string;
  Telefono: string;
  Direccion: string;
  Correo: string;
  Imagen?: string;
  Activo: boolean;
}

const useClientesApi = () => {
  const [isLoading, setIsLoading] = useState(false);
  const baseUrl = '/administracion/clientes';

  const getAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.post<ClienteItem[]>(`${baseUrl}/findAll`);
      return response.data.filter(item => !item.Eliminado);
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getById = useCallback(async (id: number) => {
    setIsLoading(true);
    try {
      const response = await api.post<ClienteDetailResponse>(`${baseUrl}/findOne/${id}`);
      return response.data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const create = useCallback(async (data: UpdateDto) => {
    setIsLoading(true);
    try {
      const response = await api.post(`${baseUrl}/create`, data);
      return response.data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const update = useCallback(async (id: number, data: ClienteDto) => {
    setIsLoading(true);
    try {
      const response = await api.post(`${baseUrl}/update/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);;

  const remove = useCallback(async (id: number) => {
    setIsLoading(true);
    try {
      await api.post(`${baseUrl}/softDelete/${id}`);
      return true;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const uploadImage = useCallback(async (file: File) => {
    const formData = new FormData();
    formData.append('imagen', file);

    try {
      const response = await api.post(`${baseUrl}/upload-imagen`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.url;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  }, []);

  // Helper function to get complete image URL
  const getImageUrl = useCallback((imagePath: string | null | undefined): string => {
    if (!imagePath) return '';
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:4020';
    return `${baseUrl}${imagePath}`;
  }, []);

  
  const getVentasCliente = useCallback(async (id: number): Promise<VentasDetailClienteResponse> => {
    setIsLoading(true);
    try {
      const response = await api.post<VentasDetailClienteResponse>(`${baseUrl}/ventas/${id}`);
      return response.data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);  

  return {
    isLoading,
    getAll,
    getById,
    create,
    update,
    remove,
    uploadImage,
    getImageUrl,
    getVentasCliente  // Nuevo método
  };
}
export { useClientesApi };
export type { ClienteItem, ClienteDto, VentaResumen, ClienteDetailResponse };